import { CURRENCY_LABEL } from '@ekaa/tokens/vocabulary';
/**
 * Payment Admin Types
 */

export type PaymentProvider = 'CINETPAY' | 'FEDAPAY' | 'EKAA_WALLET';
export type PaymentMethod =
  | 'MTN_MOBILE_MONEY'
  | 'MOOV_MONEY'
  | 'ORANGE_MONEY'
  | 'WAVE'
  | 'CARD_VISA'
  | 'CARD_MASTERCARD'
  | 'QR_CODE'
  | 'BANK_TRANSFER'
  | 'EKAA_CREDITS';

export type PaymentStatus =
  | 'PENDING'
  | 'AWAITING_PAYMENT'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED'
  | 'REFUNDED'
  | 'EXPIRED';

export type PaymentType =
  | 'SUBSCRIPTION'
  | 'ORDER'
  | 'TONTINE'
  | 'REWARD'
  | 'TRANSFER'
  | 'TOP_UP';

export interface PaymentStats {
  totalVolume: number;
  totalTransactions: number;
  successRate: number;
  averageAmount: number;
  volumeByProvider: ProviderVolume[];
  volumeByMethod: MethodVolume[];
  recentTrend: DailyTrend[];
}

export interface ProviderVolume {
  provider: PaymentProvider;
  volume: number;
  count: number;
  successRate: number;
}

export interface MethodVolume {
  method: PaymentMethod;
  volume: number;
  count: number;
}

export interface DailyTrend {
  date: string;
  volume: number;
  count: number;
}

export interface Transaction {
  id: string;
  orderId: string;
  amount: number;
  currency: string;
  status: PaymentStatus;
  provider: PaymentProvider;
  paymentMethod: PaymentMethod;
  type: PaymentType;
  description?: string;
  user?: {
    id: string;
    email: string;
    profile?: {
      displayName?: string;
      phoneNumber?: string;
    };
  };
  walletAmount?: number;
  cashAmount?: number;
  fees?: number;
  providerTransactionId?: string;
  metadata?: Record<string, any>;
  errorMessage?: string;
  webhookHistory?: WebhookEvent[];
  refunds?: Refund[];
  createdAt: string;
  updatedAt: string;
  confirmedAt?: string;
}

export interface WebhookEvent {
  id: string;
  event: string;
  payload: Record<string, any>;
  receivedAt: string;
  processed: boolean;
  processingError?: string;
}

export interface Refund {
  id: string;
  amount: number;
  reason?: string;
  status: 'PENDING' | 'COMPLETED' | 'FAILED';
  processedAt?: string;
  createdAt: string;
}

export interface ProviderComparison {
  providers: ProviderStats[];
  dailyComparison: DailyProviderComparison[];
}

export interface ProviderStats {
  provider: PaymentProvider;
  totalVolume: number;
  totalTransactions: number;
  successfulTransactions: number;
  failedTransactions: number;
  successRate: number;
  averageProcessingTime: number;
  fees: number;
}

export interface DailyProviderComparison {
  date: string;
  cinetpay: { volume: number; count: number };
  fedapay: { volume: number; count: number };
  wallet: { volume: number; count: number };
}

export interface FailedWebhook {
  id: string;
  provider: PaymentProvider;
  event: string;
  payload: Record<string, any>;
  receivedAt: string;
  processingError: string;
  retryCount: number;
  transactionId?: string;
}

export interface PaymentMethodDistribution {
  method: PaymentMethod;
  label: string;
  volume: number;
  count: number;
  percentage: number;
}

// Filter types
export interface TransactionFilter {
  status?: PaymentStatus;
  provider?: PaymentProvider;
  paymentMethod?: PaymentMethod;
  type?: PaymentType;
  userId?: string;
  minAmount?: number;
  maxAmount?: number;
  startDate?: string;
  endDate?: string;
  search?: string;
}

export interface TransactionSort {
  field: 'createdAt' | 'amount' | 'status';
  order: 'ASC' | 'DESC';
}

// Helper functions
export const PROVIDER_LABELS: Record<PaymentProvider, string> = {
  CINETPAY: 'CinetPay',
  FEDAPAY: 'FedaPay',
  EKAA_WALLET: 'EKAA Wallet',
};

export const PROVIDER_COLORS: Record<PaymentProvider, string> = {
  CINETPAY: '#FF6B35',
  FEDAPAY: '#00B894',
  EKAA_WALLET: '#D4AF37',
};

export const STATUS_LABELS: Record<PaymentStatus, string> = {
  PENDING: 'En attente',
  AWAITING_PAYMENT: 'En attente de paiement',
  PROCESSING: 'En cours',
  COMPLETED: 'Complété',
  FAILED: 'Échoué',
  CANCELLED: 'Annulé',
  REFUNDED: 'Remboursé',
  EXPIRED: 'Expiré',
};

export const STATUS_COLORS: Record<PaymentStatus, string> = {
  PENDING: '#F59E0B',
  AWAITING_PAYMENT: '#8B5CF6',
  PROCESSING: '#3B82F6',
  COMPLETED: '#10B981',
  FAILED: '#EF4444',
  CANCELLED: '#6B7280',
  REFUNDED: '#EC4899',
  EXPIRED: '#9CA3AF',
};

export const METHOD_LABELS: Record<PaymentMethod, string> = {
  MTN_MOBILE_MONEY: 'MTN Mobile Money',
  MOOV_MONEY: 'Moov Money',
  ORANGE_MONEY: 'Orange Money',
  WAVE: 'Wave',
  CARD_VISA: 'Visa',
  CARD_MASTERCARD: 'Mastercard',
  QR_CODE: 'QR Code',
  BANK_TRANSFER: 'Virement bancaire',
  EKAA_CREDITS: CURRENCY_LABEL,
};

export const TYPE_LABELS: Record<PaymentType, string> = {
  SUBSCRIPTION: 'Abonnement',
  ORDER: 'Commande',
  TONTINE: 'Tontine',
  REWARD: 'Récompense',
  TRANSFER: 'Transfert',
  TOP_UP: 'Recharge',
};

/**
 * Format amount with currency
 */
export function formatAmount(amount: number, currency: string = 'XOF'): string {
  if (currency === 'EKAA') {
    return `${amount.toLocaleString('fr-FR')} ${CURRENCY_LABEL}`;
  }
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Format percentage
 */
export function formatPercentage(value: number): string {
  return `${value.toFixed(1)}%`;
}

/**
 * Format date
 */
export function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
